import React from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useEntityList } from '../../data/entities';

const STATUS_COLOR = {
  requested: 'var(--muted)',
  launching: '#d97706',
  running: '#14e0c8',
  done: '#16a34a',
  error: '#e5484d',
  stopped: 'var(--muted)',
};

// Compute module (DevOps view): every workload in the org grouped by
// workspace. Launching new workloads happens inside the workspace page,
// where the script editor lives.
export default function ComputeModule() {
  const { org } = useOutletContext();
  const { docs: workspaces } = useEntityList('workspace', { org: org.id });

  return (
    <div data-nova-note="DevOps · compute: workloads per workspace (GPU/CPU jobs, scripts, budgets). NoVA: write_workload_script, get_run_status; starting paid compute is the person's click.">
      <h1 style={{ margin: 0 }}>DevOps <span className="mf-muted" style={{ fontWeight: 400, fontSize: 16 }}>· compute</span></h1>
      <p className="cs-help" style={{ marginTop: 6 }}>
        Every compute run in the organization, grouped by workspace: each run gets its own
        container, live logs and a hard stop. Start a new run from inside a workspace.
      </p>
      {(workspaces || []).map((ws) => (
        <WorkspaceWorkloadList key={ws.id} orgId={org.id} ws={ws} />
      ))}
      {workspaces && !workspaces.length && (
        <div className="cs-empty" style={{ marginTop: 16 }}>
          Nothing to run yet. <Link to={`/orgs/${org.id}`}>Create a workspace</Link> first — compute runs live inside one.
        </div>
      )}
    </div>
  );
}

function WorkspaceWorkloadList({ orgId, ws }) {
  const { docs: workloads } = useEntityList('workload', { org: orgId, workspace: ws.id });

  return (
    <section style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>{ws.name}</h2>
        <Link to={`/orgs/${orgId}/ws/${ws.id}`} className="cs-help" style={{ fontSize: 12 }}>
          launch workload →
        </Link>
      </div>
      {workloads && !workloads.length && (
        <p className="cs-help" style={{ marginTop: 8, fontSize: 13 }}>
          No runs yet — <Link to={`/orgs/${orgId}/ws/${ws.id}`}>open the workspace</Link> to write a script and launch one.
        </p>
      )}
      <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
        {(workloads || []).map((wl) => (
          <div key={wl.id} className="cs-card" style={{ padding: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 8, height: 8, borderRadius: 4, background: STATUS_COLOR[wl.status] || 'var(--muted)' }} />
              <strong style={{ color: 'var(--text)' }}>{wl.name}</strong>
              <span className="cs-help" style={{ fontSize: 12 }}>{wl.status}</span>
              <span style={{ flex: 1 }} />
              {wl.finishedAt && (
                <span className="cs-help" style={{ fontSize: 12 }}>
                  {new Date(wl.finishedAt).toLocaleString()}
                </span>
              )}
            </div>
            {wl.logTail?.length > 0 && (
              <pre style={{ fontSize: 11, margin: '8px 0 0', maxHeight: 96, overflow: 'auto', color: 'var(--muted)' }}>
                {wl.logTail.slice(-5).join('\n')}
              </pre>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
